import { useEffect, useState } from "react";
import { api } from "../lib/api";

// SovereignWidget — always-visible strip on the chat page. Shows:
//  • Daily spend today + cap (with "BYPASSED" marker for master)
//  • Memory usage: N/window turns; compressed count if compression fired
//  • Last compression timestamp + turns compressed (Coffin Nail: visible summariser events)
// Reads memory-live from the current session's meta event, spend + last-compress from poll.
export default function SovereignWidget({ memory }) {
  const [state, setState] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const d = await api.sovereignState();
        if (!cancelled) { setState(d); setErr(null); }
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  if (err) {
    return (
      <div
        data-testid="sw-error"
        className="border-b border-sage-warn bg-sage-bg text-[10px] uppercase tracking-[0.15em] text-sage-warn text-center py-1"
        title={err}
      >
        · Founder widget offline · {err}
      </div>
    );
  }
  if (!state) return null;

  const cap = state.daily_cap_usd || 5;
  const spent = state.spent_today_usd || 0;
  const spentPct = Math.min(100, (spent / cap) * 100);
  const bypassed = !!state.cap_bypassed;

  // Memory: prefer live meta from current session, fall back to sovereign/state thresholds.
  const total = memory?.total_prior_turns ?? 0;
  const compressed = memory?.compressed_from ?? 0;
  const window = memory?.window ?? state.memory?.window ?? 80;
  const softWarn = memory?.soft_warn_at ?? state.memory?.soft_warn_at ?? 60;
  const kept = memory?.kept_verbatim ?? total;
  const compressionActive = compressed > 0;
  const memPct = Math.min(100, (Math.min(kept, window) / window) * 100);
  const memClass = compressionActive
    ? "text-sage-accent"
    : kept >= softWarn
      ? "text-sage-warn"
      : "text-sage-muted";

  const last = state.last_compression;
  const lastLine = last?.at
    ? `${new Date(last.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} · compressed ${last.compressed_from ?? "?"} turns · $${(last.cost_usd || 0).toFixed(6)}`
    : "no compression yet";

  return (
    <div
      data-testid="sovereign-widget"
      className="border-b border-sage-border bg-sage-bg"
    >
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-2 flex items-center gap-6 flex-wrap text-[10px] uppercase tracking-[0.15em] text-sage-muted">
        {/* Spend today */}
        <div className="flex items-center gap-3" data-testid="sw-spend">
          <span className="text-sage-faint">Spend today</span>
          <span className={`tabular-nums font-bold ${bypassed ? "text-sage-accent" : "text-sage-text"}`}>
            ${spent.toFixed(4)}
          </span>
          {bypassed ? (
            <span
              className="border border-sage-accent text-sage-accent px-2 py-0.5"
              title="Founder's account bypasses the daily cap."
              data-testid="sw-cap-bypassed"
            >
              cap bypassed
            </span>
          ) : (
            <div className="w-24 h-1.5 border border-sage-border bg-sage-elevated">
              <div
                className={`h-full ${spentPct >= 100 ? "bg-sage-err" : spentPct >= 80 ? "bg-sage-warn" : "bg-sage-accent"}`}
                style={{ width: `${spentPct}%` }}
              />
            </div>
          )}
          <span className="text-sage-faint tabular-nums">/ ${cap.toFixed(2)} cap</span>
        </div>

        <span className="text-sage-faint">·</span>

        {/* Memory usage */}
        <div className="flex items-center gap-3" data-testid="sw-memory">
          <span className="text-sage-faint">Memory</span>
          <div className="w-24 h-1.5 border border-sage-border bg-sage-elevated">
            <div
              className={compressionActive ? "h-full bg-sage-accent" : kept >= softWarn ? "h-full bg-sage-warn" : "h-full bg-sage-muted"}
              style={{ width: `${memPct}%` }}
            />
          </div>
          <span className={`tabular-nums font-bold ${memClass}`}>
            {kept}/{window}
          </span>
          {compressionActive && (
            <span
              className="border border-sage-accent text-sage-accent px-2 py-0.5"
              title={`${compressed} older turns already summarised into a compact memory block.`}
              data-testid="sw-memory-compressed"
            >
              compressed {compressed}
            </span>
          )}
          {!compressionActive && kept >= softWarn && (
            <span className="text-sage-warn tabular-nums" title={`Compression fires at ${window} turns.`}>
              → {window - kept} to compress
            </span>
          )}
        </div>

        <span className="text-sage-faint hidden md:inline">·</span>

        {/* Last compression event */}
        <div className="flex items-center gap-2 hidden md:flex" data-testid="sw-last-compress" title="Every compressor run is logged to the Ledger as a machine receipt.">
          <span className="text-sage-faint">Last compress</span>
          <span className="text-sage-text tabular-nums">{lastLine}</span>
        </div>
      </div>
    </div>
  );
}
